import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { NavHelperService } from '../nav/nav-helper.service';
import { PageArgs } from '../../models/projects/operation.models';
import { SupportContext, SupportContextKey } from '../../models/support/support.models';
import { DailyLogService } from '../daily-log/daily-log.service';
import { AuthService } from '../../_foundation/services/auth/auth.service';

@Injectable()
export class SupportContextService {
  private contextSharedObj = new BehaviorSubject<SupportContext>(new SupportContext());
  private contextUpdateVersion = 0;
  private currentNavPage: PageArgs;
  currentContext: Observable<SupportContext> = this.contextSharedObj.asObservable();

  constructor(
    private navHelperService: NavHelperService,
    private dailyLogService: DailyLogService,
    private authService: AuthService
  ) {
    this.navHelperService.currentNavPage.subscribe(navPage => {
      this.currentNavPage = navPage;
      this.setContextFromNavPage(navPage);
    });
  }

  setContext(context: SupportContext) {
    this.setContextWithDailyLogDate(context || new SupportContext());
  }

  setComponentContext(
    key: SupportContextKey,
    label: string,
    componentName?: string,
    details?: {[key: string]: string},
    additionalSuggestedContextKeys?: SupportContextKey[]
  ) {
    const context = new SupportContext();
    context.key = key;
    context.label = label;
    context.componentName = componentName;
    context.details = details;
    context.additionalSuggestedContextKeys = additionalSuggestedContextKeys;

    this.setContext(context);
  }

  getCurrentContext(): SupportContext {
    return this.contextSharedObj.getValue();
  }

  restoreCurrentNavContext() {
    this.setContextFromNavPage(this.currentNavPage);
  }

  async refreshCurrentContextDetails(): Promise<SupportContext> {
    const context = this.getCurrentContext() || new SupportContext();
    await this.setContextWithDailyLogDate(context);

    return this.getCurrentContext();
  }

  private setContextFromNavPage(navPage: PageArgs) {
    if (navPage && navPage.supportContextKey && navPage.supportContextLabel) {
      const context = this.createContext(navPage.supportContextKey as SupportContextKey, navPage.supportContextLabel);
      context.pageName = navPage.page && navPage.page.name ? navPage.page.name : null;

      this.setContext(context);
      return;
    }

    const pageName = navPage && navPage.page ? navPage.page.name : null;
    const context = this.mapPageNameToContext(pageName);
    context.pageName = pageName;

    this.setContext(context);
  }

  private mapPageNameToContext(pageName: string): SupportContext {
    switch (pageName) {
      case 'LoginPage':
        return this.createContext(SupportContextKey.Login, 'Login');
      case 'DailyLogPage':
        return this.createContext(SupportContextKey.DailyLogSummary, 'Daily Log');
      case 'DailyLogSummaryPage':
        return this.createContext(SupportContextKey.DailyLogSummary, 'Daily Log Summary');
      case 'LogEventsPage':
        return this.createContext(SupportContextKey.AddToDailyLog, 'Add to Daily Log');
      case 'OperationDetailsPage':
        return this.createContext(SupportContextKey.AddToDailyLog, 'Operation Add');
      case 'OperationDetailsEditPage':
      case 'OperationDetailsEditLocationSelectPage':
      case 'OperationGenericCommentsPage':
      case 'OperationCommentsPage':
      case 'StepCommentsPage':
      case 'OperationSplitPage':
        return this.createContext(SupportContextKey.EditOperation, 'Edit Operation Screen');
      case 'DailyLegLoadsLogPage':
      case 'DailyLegLoadEventsPage':
      case 'DailyLegLoadManageRecordingsPage':
      case 'DailyLegLoadsManageReadingsPage':
        return this.createContext(SupportContextKey.LegLoads, 'Leg Loads');
      case 'WeatherLogPage':
      case 'WeatherEventsPage':
      case 'WeatherReadingCreatePage':
      case 'WeatherLogManageRecordingsPage':
      case 'WeatherLogManageReadingsPage':
        return this.createContext(SupportContextKey.WeatherLog, 'Weather Log');
      case 'DprSectionsEventsPage':
      case 'ConsumableEditPage':
      case 'DprAttachmentsEditPage':
      case 'DailyObservationEditPage':
      case 'SafetyEditPage':
      case 'SafetyReportEditPage':
      case 'VesselCrewEditPage':
      case 'VesselCrewOvertimeEditPage':
        return this.createContext(SupportContextKey.DprSections, 'DPR Sections');
      case 'JackingActivitiesRouterPage':
      case 'JackingActivityEditPage':
      case 'JackingActivityEditV2Page':
      case 'JackingActivityV2EditPage':
      case 'JackingActivityEditCompletePage':
      case 'JackingActivityEditCancelPage':
        return this.createContext(SupportContextKey.JackingActivities, 'Jacking Activities');
      case 'DailyProgressReportPreviewPage':
        return this.createContext(SupportContextKey.CompleteDay, 'DPR Preview');
      case 'VoyageLogPage':
        return this.createContext(SupportContextKey.VoyageLog, 'Voyage Log');
      case 'MorningMinutesPage':
        return this.createContext(SupportContextKey.MorningMinutes, 'MoM');
      case 'ConfigureProjectPage':
        return this.createContext(SupportContextKey.EditProject, 'Edit Project');
      case 'TimezoneChangePage':
        return this.createContext(SupportContextKey.ChangeTimeZone, 'Change Time Zone');
      case 'ImplementerNavigatorPage':
      case 'ImplementerInitialiserPage':
      case 'ImplementerMergePage':
      case 'ImplementerCancelPage':
      case 'ImplementerEmptyStatePage':
        return this.createContext(SupportContextKey.ImplementerMode, 'Implementer Mode');
      case 'ChangeHistoryPage':
        return this.createContext(SupportContextKey.ChangeHistory, 'Change History');
      default:
        return new SupportContext();
    }
  }

  private createContext(key: SupportContextKey, label: string): SupportContext {
    const context = new SupportContext();
    context.key = key;
    context.label = label;

    return context;
  }

  private async setContextWithDailyLogDate(context: SupportContext) {
    const version = ++this.contextUpdateVersion;

    try {
      const dailyLogDate = await this.getDailyLogDateContextDetail(context);
      const details = Object.assign({}, context.details || {});

      if (dailyLogDate) {
        details.dailyLogDate = dailyLogDate;
      } else {
        delete details.dailyLogDate;
      }

      context.details = Object.keys(details).length ? details : undefined;
    } catch (e) {
      // Context should still update if the current daily log date cannot be read.
    }

    if (version === this.contextUpdateVersion) {
      this.contextSharedObj.next(context);
    }
  }

  private async getDailyLogDateContextDetail(context: SupportContext): Promise<string> {
    if (!context || context.key === SupportContextKey.Login || !this.authService.isAuthenticated) {
      return '';
    }

    const dailyLogDate = await this.dailyLogService.getCurrentLogDay(false, true);

    if (!dailyLogDate || !dailyLogDate.dateTime) {
      return '';
    }

    return dailyLogDate.dateTime.toFormat('dd/MM/yyyy');
  }
}



// WEBPACK FOOTER //
// ./src/services/support/support-context.service.ts